import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

import { readStorage, writeStorage } from './assistant-storage'
import {
  clampToArea,
  parseAnchor,
  placementArea,
  resolveAnchor,
  snapToNearestEdge,
  type EdgeAnchor,
  type Point,
  type Size,
} from './edge-anchor'
import { useViewportSize } from './useViewportSize'

/** Movement past this many pixels turns a press into a drag — and swallows the click that ends it. */
export const DRAG_THRESHOLD_PX = 4

export interface EdgeAnchoredDragOptions {
  /** Where the snapped anchor is remembered. */
  storageKey: string
  /** The element's size in a given viewport. Pass a module-level function. */
  sizeFor: (viewport: Size) => Size
  /** The anchor used when nothing valid is stored. Read once, on mount. */
  defaultAnchor: (viewport: Size) => EdgeAnchor
}

export interface EdgeAnchoredDrag {
  /** Top-left in viewport coordinates — apply as `left` / `top` on a `fixed` element. */
  point: Point
  /** The element's size in the current viewport. */
  size: Size
  dragging: boolean
  /** Attach to the drag handle. */
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
  /** Read-and-reset: whether the most recent press was a drag. */
  wasDragged: () => boolean
}

function currentViewport(): Size {
  return { width: window.innerWidth, height: window.innerHeight }
}

/**
 * Drag a `fixed` element, snap it to the nearest edge when it is let go, and keep it stuck to that
 * edge as the window changes size.
 *
 * The ANCHOR is the state, never a point. The point is recomputed from it on every render against
 * the current viewport (`useViewportSize` re-renders on `resize`), which is the whole of the
 * stickiness — there is no resize handler that moves anything.
 *
 * Window listeners, not React handlers, follow the gesture so it survives the pointer outrunning the
 * element; pointer capture, where available, keeps the moves arriving while the pointer crosses a
 * map canvas or an iframe, either of which would otherwise swallow them.
 */
export function useEdgeAnchoredDrag({ storageKey, sizeFor, defaultAnchor }: EdgeAnchoredDragOptions): EdgeAnchoredDrag {
  const viewport = useViewportSize()
  const [anchor, setAnchor] = useState<EdgeAnchor>(
    () => parseAnchor(readStorage(storageKey)) ?? defaultAnchor(viewport),
  )
  const [dragPoint, setDragPoint] = useState<Point | null>(null)
  const draggedRef = useRef(false)
  const teardownRef = useRef<(() => void) | null>(null)

  // A gesture still live at unmount (the window closed mid-drag) must not leave listeners behind.
  useEffect(() => () => teardownRef.current?.(), [])

  const size = sizeFor(viewport)
  const point = dragPoint ?? resolveAnchor(anchor, size, placementArea(viewport))

  function onPointerDown(event: ReactPointerEvent<HTMLElement>) {
    // A secondary button (right-click to inspect) is not a drag.
    if (event.button > 0) return

    // A press while a gesture is still live (a second finger, or a release that never arrived)
    // replaces it: its listeners go, and so does its unfinished point — kept, it would strand the
    // element mid-screen, off every edge, until the next drag.
    teardownRef.current?.()
    setDragPoint(null)
    draggedRef.current = false

    // Only the pointer that pressed drives the gesture; another finger on the screen does not.
    const { pointerId } = event
    const origin = { x: event.clientX, y: event.clientY }
    // Where in the element it was grabbed, so it does not jump to put its corner under the pointer.
    const grab = { x: event.clientX - point.x, y: event.clientY - point.y }
    let last: Point | null = null

    try {
      event.currentTarget.setPointerCapture(pointerId)
    } catch {
      // No capture (jsdom, or the pointer is already gone): the window listeners still follow it.
    }

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return
      if (!draggedRef.current && Math.hypot(e.clientX - origin.x, e.clientY - origin.y) <= DRAG_THRESHOLD_PX) return

      draggedRef.current = true
      const vp = currentViewport()
      last = clampToArea({ x: e.clientX - grab.x, y: e.clientY - grab.y }, sizeFor(vp), placementArea(vp))
      setDragPoint(last)
    }

    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return

      teardownRef.current?.()
      if (!last) return

      const vp = currentViewport()
      const snapped = snapToNearestEdge(last, sizeFor(vp), placementArea(vp))

      setAnchor(snapped)
      setDragPoint(null)
      writeStorage(storageKey, JSON.stringify(snapped))
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    teardownRef.current = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      teardownRef.current = null
    }
  }

  return {
    point,
    size,
    dragging: dragPoint !== null,
    onPointerDown,
    /**
     * Consumes the drag flag: whether the most recent press was a drag, cleared on read.
     *
     * Read-and-reset, never read-only. A keyboard activation (Enter / Space) dispatches `click` with
     * no `pointerdown` before it, so nothing else would clear a flag a real drag left set — the
     * launcher would stay unopenable from the keyboard after a single drag, until it remounted.
     */
    wasDragged: () => {
      const dragged = draggedRef.current
      draggedRef.current = false

      return dragged
    },
  }
}
